import React from 'react';
import PropTypes from 'prop-types';
import { Doughnut } from 'react-chartjs-2';
import { Row, Col } from 'react-bootstrap';
import withStyles from 'isomorphic-style-loader/lib/withStyles';
// eslint-disable-next-line css-modules/no-unused-class
import moment from 'moment';
import s from './EnvMetrics.scss';

const formatValue = (value, type, isAverage) => {
  switch (type) {
    case 'number':
      if (typeof value !== 'number') return '-';
      if (isAverage) {
        return (value / 12).toFixed(0);
      }
      return value.toFixed(0);
    case 'time': {
      if (typeof value !== 'string') return '-';

      if (!isAverage) {
        return value
          .split(':')
          .filter(
            segment => !segment.startsWith('00') && !segment.endsWith('s'),
          )
          .join(' ');
      }

      if (typeof value !== 'string') return '-';

      const hMatch = value.match(/(\d+)h/);
      const mMatch = value.match(/(\d+)m/);
      const sMatch = value.match(/(\d+)s/);

      let duration = moment.duration({
        hours: hMatch ? Number(hMatch[1]) : 0,
        minutes: mMatch ? Number(mMatch[1]) : 0,
        seconds: sMatch ? Number(sMatch[1]) : 0,
      });

      duration = moment.duration(duration.asMilliseconds() / 12);

      return moment
        .utc(duration.asMilliseconds())
        .format('HH[h]:mm[m]:ss[s]')
        .split(':')
        .filter(segment => !segment.startsWith('00') && !segment.endsWith('s'))
        .join(' ');
    }
    default:
      throw new TypeError("Either 'number' or 'time' type expected.");
  }
};

const renderEnvMetrics = (label, unit, metrics) => {
  if (!metrics) return null;

  return (
    <Row className={s.itemMetricGroup}>
      <Row className={s.cardItem}>
        <Col md={12} className={s.cardSubTitle}>
          {label}
        </Col>
      </Row>
      <Row className={`${s.cardItem} ${s.cardItemEnvSubtitle}`}>
        <Col md={4} className={`${s.displayName} ${s.alignLeft}`}>
          min
        </Col>
        <Col md={4} className={`${s.displayName} ${s.alignCenter}`}>
          mean
        </Col>
        <Col md={4} className={`${s.displayName} ${s.alignRight}`}>
          max
        </Col>
      </Row>
      <Row className={s.cardItem}>
        <Col md={4} className={`${s.alignLeft} ${s.biggerText}`}>
          {formatValue(metrics.min, 'number')} {unit}
        </Col>
        <Col md={4} className={`${s.alignCenter} ${s.biggerText}`}>
          {formatValue(metrics.mean, 'number')} {unit}
        </Col>
        <Col md={4} className={`${s.alignRight} ${s.biggerText}`}>
          {formatValue(metrics.max, 'number')} {unit}
        </Col>
      </Row>
    </Row>
  );
};

class ReportDataMetrics extends React.Component {
  static propTypes = {
    title: PropTypes.string.isRequired,
    loading: PropTypes.bool.isRequired,
    data: PropTypes.arrayOf(
      PropTypes.shape({
        powerTotal: PropTypes.number,
        lightHoursTotal: PropTypes.string,
        mainDoorOpenHoursTotal: PropTypes.string,
        allWindowsOpenHoursTotal: PropTypes.string,
        co2Metrics: PropTypes.object,
        temperatureMetrics: PropTypes.object,
        humidityMetrics: PropTypes.object,
      }),
    ),
    average: PropTypes.bool,
  };

  static defaultProps = {
    data: [],
    average: false,
  };

  render() {
    const { loading, data, average } = this.props;
    const reportData = data.length > 0 ? data[0] : null;

    if (loading || !reportData) {
      return null;
    }

    const graphData = {
      labels: ['In Office', 'Home Office'],
      datasets: [
        {
          data: [
            average
              ? reportData.officeWorkHoursAvg.toFixed(2)
              : reportData.officeWorkHoursTotal.toFixed(2),
            average
              ? reportData.homeOfficeWorkHoursAvg.toFixed(2)
              : reportData.homeOfficeWorkHoursTotal.toFixed(2),
          ],
          backgroundColor: [
            'rgba(255, 99, 132, 0.6)',
            'rgba(255, 206, 86, 0.6)',
          ],
          borderColor: ['rgba(255, 99, 132, 1)', 'rgba(255, 206, 86, 1)'],
          borderWidth: 1,
          hoverOffset: 4,
        },
      ],
    };

    return (
      <div className={s.card}>
        <div className={s.cardTitle}>{this.props.title}</div>
        <div className={s.cardItemsWrapper}>
          <Row>
            <Col md={6}>
              <div style={{ margin: '1em 0 1em 0' }}>
                <Doughnut
                  name="homeOfficeChart"
                  data={graphData}
                  options={{
                    responsive: true,
                    maintainAspectRatio: true,
                    plugins: {
                      datalabels: {
                        display:
                          (average
                            ? reportData.officeWorkHoursAvg
                            : reportData.officeWorkHoursTotal) > 15 ||
                          (average
                            ? reportData.homeOfficeWorkHoursAvg
                            : reportData.homeOfficeWorkHoursTotal) > 15,
                      },
                    },
                  }}
                />
              </div>
              <Row className={s.cardItem}>
                <Col md={7} className={`${s.displayName} ${s.alignLeft}`}>
                  Power consumption
                </Col>
                <Col md={5} className={`${s.alignRight} ${s.biggerText}`}>
                  {formatValue(reportData.powerTotal, 'number', average)} kWh
                </Col>
              </Row>
              <Row className={s.cardItem}>
                <Col md={7} className={`${s.displayName} ${s.alignLeft}`}>
                  Average power draw
                </Col>
                <Col md={5} className={`${s.alignRight} ${s.biggerText}`}>
                  <span>
                    {(
                      reportData.powerTotal /
                      (reportData.officeWorkHoursTotal || 1) /
                      (average ? 12 : 1) *
                      1000
                    ).toFixed(2)}
                  </span>{' '}
                  W
                </Col>
              </Row>
              <Row className={s.cardItem}>
                <Col md={7} className={`${s.displayName} ${s.alignLeft}`}>
                  Light on
                </Col>
                <Col md={5} className={`${s.alignRight} ${s.biggerText}`}>
                  {formatValue(reportData.lightHoursTotal, 'time', average)}
                </Col>
              </Row>
              <Row className={s.cardItem}>
                <Col md={7} className={`${s.displayName} ${s.alignLeft}`}>
                  Door open
                </Col>
                <Col md={5} className={`${s.alignRight} ${s.biggerText}`}>
                  {formatValue(
                    reportData.mainDoorOpenHoursTotal,
                    'time',
                    average,
                  )}
                </Col>
              </Row>
              <Row className={s.cardItem}>
                <Col md={7} className={`${s.displayName} ${s.alignLeft}`}>
                  Windows open
                </Col>
                <Col md={5} className={`${s.alignRight} ${s.biggerText}`}>
                  {formatValue(
                    reportData.allWindowsOpenHoursTotal,
                    'time',
                    average,
                  )}
                </Col>
              </Row>
            </Col>

            <Col md={6}>
              {renderEnvMetrics('CO₂', 'ppm', reportData.co2Metrics)}
              {renderEnvMetrics(
                'Temperature',
                '°C',
                reportData.temperatureMetrics,
              )}
              {renderEnvMetrics('Humidity', '%', reportData.humidityMetrics)}
            </Col>
          </Row>
        </div>
      </div>
    );
  }
}

export default withStyles(s)(ReportDataMetrics);
